import {useState, useCallback, useMemo} from "react";
import {
	GridFilterModel,
	GridSortModel,
	GridInitialState,
	type GridFilterItem,
	GridCallbackDetails
} from "@mui/x-data-grid-pro";
import {
	useListCourseEnrollments,
	CoursesControllerListEnrollmentsParams,
	type CourseEnrollmentAttributeFilterDto,
	type CourseEnrollmentAttributeFilterDtoField,
	type CourseEnrollmentFilterCondition,
	COURSES_QUERY_KEYS,
	DEFAULT_STANDARD_ADMIN_LIST_PAGE_SIZE
} from "@/app2/shared/data";
import { useQueryClient } from "@tanstack/react-query";

/** API-supported filter fields (aligned with `CourseEnrollmentAttributeFilterDtoField`). */
const FILTERABLE_FIELD_LIST: readonly CourseEnrollmentAttributeFilterDtoField[] = [
	"firstname",
	"lastname",
	"email",
	"jobTitle",
	"jobCategory",
	"division",
	"supervisorName",
	"employeeCode",
	"customFieldValue1",
	"customFieldValue2",
	"customFieldValue3",
	"progress",
	"enrolled",
	"enrollmentType",
	"hireDate",
	"titleStartDate",
	"dueDate",
	"assignedOn"
];

const SORT_FIELDS: readonly string[] = [...FILTERABLE_FIELD_LIST];

const DEFAULT_SORT_BY = "lastname";
const DEFAULT_SORT_DIRECTION: "asc" | "desc" = "asc";

/**
 * Map MUI filter operator to improve-api short codes.
 * MUI stores the operator on `operatorValue` (see GridFilterItem), not `operator`.
 * @see TeamProgressPage/utils/filters.ts (same MUI → API mapping pattern)
 */
function mapGridOperatorToApi(operator: string | undefined): CourseEnrollmentAttributeFilterDto["operator"] {
	if (!operator) return "co";
	if (operator === "isNotEmpty") return "pr";
	const apiShortCodes: Record<string, CourseEnrollmentAttributeFilterDto["operator"]> = {
		co: "co",
		eq: "eq",
		ne: "ne",
		sw: "sw",
		ew: "ew",
		pr: "pr",
		gt: "gt",
		lt: "lt",
		ge: "ge",
		le: "le",
		is: "is"
	};
	if (apiShortCodes[operator]) return apiShortCodes[operator];
	const lower = operator.toLowerCase().trim();
	/** Verbose labels some MUI versions use (aligned with Team Progress normalizeOperator). */
	const verbose: Record<string, CourseEnrollmentAttributeFilterDto["operator"]> = {
		equals: "eq",
		doesnotequal: "ne",
		contains: "co",
		doesnotcontain: "ne",
		startswith: "sw",
		endswith: "ew",
		lessthan: "lt",
		greaterthan: "gt",
		lessthanorequal: "le",
		greaterthanorequal: "ge",
		isnotempty: "pr"
	};
	if (verbose[lower]) return verbose[lower];
	const m: Record<string, CourseEnrollmentAttributeFilterDto["operator"]> = {
		contains: "co",
		equals: "eq",
		startsWith: "sw",
		endsWith: "ew",
		"=": "eq",
		"!=": "ne",
		">": "gt",
		">=": "ge",
		"<": "lt",
		"<=": "le"
	};
	return m[operator] ?? "co";
}

interface UseCourseEnrollmentsGridProps {
	courseId: number;
	cohortId: number;
	selected: number[];
	setSelected: (ids: number[]) => void;
	initialFilterSettings?: CoursesControllerListEnrollmentsParams;
	onFilterModelChange?: (filterModel: GridFilterModel, details: GridCallbackDetails<"filter">) => void;
}

export const useCourseEnrollmentsGrid = ({
	courseId,
	cohortId,
	selected,
	setSelected,
	initialFilterSettings,
	onFilterModelChange
}: UseCourseEnrollmentsGridProps) => {
	const queryClient = useQueryClient();
	const [filterModel, setFilterModel] = useState<CoursesControllerListEnrollmentsParams>(
		initialFilterSettings ?? {
			page: 1,
			pageSize: DEFAULT_STANDARD_ADMIN_LIST_PAGE_SIZE,
			sortBy: DEFAULT_SORT_BY,
			sortDirection: DEFAULT_SORT_DIRECTION
		}
	);

	const {data, isFetching, refetch} = useListCourseEnrollments(courseId, cohortId, filterModel);

	const enrollments = data?.data ?? [];
	const rowCount = data?.total ?? 0;

	const onPageChange = useCallback((page: number) => {
		setFilterModel((prev) => ({
			...prev,
			page: page + 1
		}));
	}, []);

	const onPageSizeChange = useCallback((pageSize: number) => {
		setFilterModel((prev) => ({
			...prev,
			pageSize,
			page: 1
		}));
	}, []);

	const onFilterChange = useCallback(
		(newFilterModel: GridFilterModel, details: GridCallbackDetails<"filter">) => {
			setFilterModel((prev) => {
				const filters: CourseEnrollmentFilterCondition[] = (newFilterModel.items ?? [])
					.filter((item: GridFilterItem & {columnField?: string; field?: string}) => {
						const field = item.columnField ?? item.field;
						const muiOperator = item.operatorValue ?? (item as {operator?: string}).operator;
						const muiOpLower = (muiOperator ?? "").toLowerCase();
						if (muiOpLower === "isempty") {
							return false;
						}
						const op = mapGridOperatorToApi(muiOperator);
						const hasValue =
							item.value !== undefined &&
							item.value !== null &&
							String(item.value).trim() !== "";
						const allowZero =
							field === "progress" && (item.value === 0 || item.value === "0");
						return (
							!!field &&
							FILTERABLE_FIELD_LIST.includes(field as CourseEnrollmentAttributeFilterDtoField) &&
							(op === "pr" || hasValue || allowZero)
						);
					})
					.map((item: GridFilterItem & {columnField?: string; field?: string}) => {
						const field = (item.columnField ?? item.field) as CourseEnrollmentAttributeFilterDtoField;
						const muiOperator = item.operatorValue ?? (item as {operator?: string}).operator;
						const operator = mapGridOperatorToApi(muiOperator);
						let value: string | undefined;
						if (operator === "pr") {
							value = undefined;
						} else if (item.value instanceof Date) {
							value = item.value.toISOString().split("T")[0];
						} else if (typeof item.value === "boolean") {
							value = item.value ? "true" : "false";
						} else if (field === "progress") {
							value = String(Number(item.value));
						} else {
							if (Date.parse(item.value ?? "")){
								value = new Date(item.value).toISOString().split("T")[0];
							} else {
								value = String(item.value ?? "");
							}
						}
						return {field, value, operator} satisfies CourseEnrollmentFilterCondition;
					});

				onFilterModelChange?.(newFilterModel, details);

				const linkOp = newFilterModel.linkOperator === "or" ? "or" : "and";

				return {
					...prev,
					page: 1,
					filters: filters.length > 0 ? {operator: linkOp, conditions: filters} : undefined
				};
			});
		},
		[onFilterModelChange]
	);

	const onSortModelChange = useCallback((sortModel: GridSortModel) => {
		setFilterModel((prev) => ({
			...prev,
			sortBy:
				sortModel.length > 0 && SORT_FIELDS.includes(sortModel[0].field)
					? sortModel[0].field
					: DEFAULT_SORT_BY,
			sortDirection:
				sortModel.length > 0 ? (sortModel[0].sort as "asc" | "desc") : DEFAULT_SORT_DIRECTION
		}));
	}, []);

	const sortModel = useMemo<GridSortModel>(
		() => [
			{
				field: filterModel.sortBy ?? DEFAULT_SORT_BY,
				sort: filterModel.sortDirection ?? DEFAULT_SORT_DIRECTION
			}
		],
		[filterModel.sortBy, filterModel.sortDirection]
	);

	const onRowSelectionChange = useCallback(
		(newSelection: number[]) => {
			setSelected(newSelection);
		},
		[setSelected]
	);

	const defaultInitialState: GridInitialState = {
		pagination: {
			pageSize: filterModel.pageSize ?? DEFAULT_STANDARD_ADMIN_LIST_PAGE_SIZE,
			page: (filterModel.page ?? 1) - 1
		},
		sorting: {
			sortModel: [{field: DEFAULT_SORT_BY, sort: DEFAULT_SORT_DIRECTION}]
		},
		columns: {
			columnVisibilityModel: {
				email: false,
				supervisorName: false,
				hireDate: false,
				titleStartDate: false
			}
		}
	};

	const refetchCourseData = useCallback(() => {
		queryClient.invalidateQueries({ queryKey: [COURSES_QUERY_KEYS.LIST] });
		queryClient.invalidateQueries({ queryKey: [COURSES_QUERY_KEYS.ADMIN_COURSE, courseId] });
	}, [queryClient]);

	return {
		enrollments,
		rowCount,
		isFetching,
		refetch,
		page: (filterModel.page ?? 1) - 1,
		pageSize: filterModel.pageSize ?? DEFAULT_STANDARD_ADMIN_LIST_PAGE_SIZE,
		onPageChange,
		onPageSizeChange,
		onFilterChange,
		sortModel,
		onSortModelChange,
		onRowSelectionChange,
		defaultInitialState,
		refetchCourseData
	};
};
